import React, { useState, useEffect, useRef } from 'react'; // useRef und useEffect importiert
import axios from 'axios';
import { IoMdArrowForward, IoMdArrowBack } from "react-icons/io";

// Constants
const POLLS_API_BASE_URL = '/poll'; // API-Endpunkt (ggf. aus einer globalen Config importieren)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Regex für E-Mail-Validierung

const ParticipantForm = ({ pollId, onClose, fetchPollData }) => {
    const [formStep, setFormStep] = useState(1);
    const [participantFormData, setParticipantFormData] = useState({
        email: '',
        firstname: '',
        lastname: '',
        participation: true,
        guests: 0
    });
    const [formError, setFormError] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [stepLoading, setStepLoading] = useState(false);

    // Refs für die Eingabefelder
    const emailInputRef = useRef(null);
    const firstnameInputRef = useRef(null);
    const guestsInputRef = useRef(null);

    // useEffect, um den Fokus zu setzen, wenn sich der formStep ändert
    useEffect(() => {
        if (formStep === 1 && emailInputRef.current) {
            emailInputRef.current.focus();
        } else if (formStep === 2 && firstnameInputRef.current) {
            firstnameInputRef.current.focus();
        } else if (formStep === 3 && guestsInputRef.current) {
            guestsInputRef.current.focus();
        }
    }, [formStep]); // Läuft, wenn formStep sich ändert

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setParticipantFormData(prev => ({ ...prev, [name]: value }));
        setFormError(null);
    };

    const validateAndProceed = async () => {
        setFormError(null);

        if (formStep === 1) {
            if (!participantFormData.email || !EMAIL_REGEX.test(participantFormData.email)) {
                setFormError('Bitte gib eine gültige E-Mail-Adresse ein (z.B. name@domain.de).');
                emailInputRef.current?.focus();
                return; 
            }
            
            setStepLoading(true);
            let emailCheckPassed = false;
            try {
                const response = await axios.get(`${POLLS_API_BASE_URL}/check-email/${encodeURIComponent(participantFormData.email)}`);
                if (response.data.isTaken) {
                    setFormError('Diese E-Mail-Adresse ist bereits registriert. Bitte verwende eine andere.');
                    emailInputRef.current?.focus();
                } else {
                    emailCheckPassed = true; // Email ist verfügbar
                }
            } catch (err) {
                console.error("Fehler bei der E-Mail-Prüfung:", err);
                setFormError(err.response?.data?.message || 'Fehler bei der E-Mail-Prüfung. Versuche es später erneut.');
                emailInputRef.current?.focus();
            } finally {
                setStepLoading(false);
            }

            if (emailCheckPassed) { // Nur zum nächsten Schritt gehen, wenn die E-Mail-Prüfung erfolgreich war
                setFormStep(currentStep => currentStep + 1);
            }
            // Wenn emailCheckPassed false ist, wurde bereits ein Fehler gesetzt und die Funktion endet hier.

        } else if (formStep === 2) {
            if (!participantFormData.firstname || !participantFormData.lastname) {
                setFormError('Bitte gib deinen Vor- und Nachnamen ein.');
                firstnameInputRef.current?.focus();
                return; 
            }
            // Namen sind valide, gehe zum nächsten Schritt
            setFormStep(currentStep => currentStep + 1); 
        }
        // Für formStep >= 3 wird diese Funktion nicht mehr für den Schrittwechsel verwendet.
    };
    
    const handleNextStepButtonClick = () => { 
        if (stepLoading || submitLoading) return;
        validateAndProceed();
    };

    const handlePrevStep = () => {
        if (stepLoading || submitLoading) return;
        setFormError(null);
        setFormStep(formStep - 1);
    };

    const executeActualSubmit = async () => {
        if (formStep !== 3) {
            console.warn("executeActualSubmit called on incorrect step:", formStep);
            setFormError("Ein interner Fehler ist aufgetreten (Step Mismatch). Bitte versuchen Sie es erneut.");
            return; // submitLoading wird nicht gesetzt, da die Funktion hier endet
        }

        const guestCount = parseInt(participantFormData.guests, 10);
        if (isNaN(guestCount) || guestCount < 0 || guestCount >= 10) {
            setFormError('Die Anzahl der Gäste muss eine positive Zahl, null oder kleiner als 10 sein.');
            guestsInputRef.current?.focus();
            return;
        }

        if (!participantFormData.email || !participantFormData.firstname || !participantFormData.lastname) {
            setFormError('Bitte fülle alle erforderlichen Felder aus.');
            if (!participantFormData.email && emailInputRef.current) {
                setFormStep(1); 
            } else if ((!participantFormData.firstname || !participantFormData.lastname) && firstnameInputRef.current ) {
                setFormStep(2); 
            }
            return;
        }

        setFormError(null);
        setSubmitLoading(true);
        try {
            await axios.post(`${POLLS_API_BASE_URL}/${pollId}/participants`, {
                ...participantFormData,
                guests: guestCount
            }, { withCredentials: true });
            onClose();
            fetchPollData();
        } catch (err) {
            console.error("Fehler beim Hinzufügen der Teilnahme:", err);
            setFormError(err.response?.data?.message || 'Ein Fehler ist beim Speichern deiner Teilnahme aufgetreten.');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleFinalSubmitButtonClick = () => { 
        if (stepLoading || submitLoading) return;
        executeActualSubmit();
    };

    const handleFormSubmit = (event) => {
        event.preventDefault(); 
        if (submitLoading || stepLoading) return; 

        if (formStep < 3) {
            validateAndProceed(); 
        } else if (formStep === 3) {
            executeActualSubmit(); 
        }
    };

    return (
        <div className="mt-4 p-3 p-md-4 border rounded bg-light shadow-sm">
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <h2 className='fw-semibold rakete-font text-danger fs-5 text-uppercase mb-0'>
                    {formStep === 1 && "Deine Email"}
                    {formStep === 2 && "Deine Daten"}
                    {formStep === 3 && "Deine Gäste"}
                </h2>
                <button type="button" className="btn-close" onClick={onClose} title="Formular schließen" disabled={submitLoading || stepLoading}></button>
            </div>

            {formError && <div className="alert alert-danger fs-7 rakete-font p-2">{formError}</div>}

            <form onSubmit={handleFormSubmit}> 
                {formStep === 1 && (
                    <div className="mb-3">
                        <label htmlFor="formEmail" className="form-label fs-7 rakete-font">Email-Adresse</label>
                        <input 
                            ref={emailInputRef} 
                            type="email" 
                            className="form-control" 
                            id="formEmail" 
                            name="email" 
                            value={participantFormData.email} 
                            onChange={handleFormChange} 
                            required 
                            disabled={submitLoading || stepLoading} 
                        />
                        <div className="form-text fs-8">*Diese Email wird verwendet um deinen Eintrag zu verwalten.</div>
                    </div>
                )}
                {formStep === 2 && (
                    <>
                        <div className="mb-3">
                            <label htmlFor="formFirstname" className="form-label fs-7 rakete-font">Vorname</label>
                            <input 
                                ref={firstnameInputRef} 
                                type="text" 
                                className="form-control" 
                                id="formFirstname" 
                                name="firstname" 
                                value={participantFormData.firstname} 
                                onChange={handleFormChange} 
                                required 
                                disabled={submitLoading} 
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="formLastname" className="form-label fs-7 rakete-font">Nachname</label>
                            <input 
                                type="text" 
                                className="form-control" 
                                id="formLastname" 
                                name="lastname" 
                                value={participantFormData.lastname} 
                                onChange={handleFormChange} 
                                required 
                                disabled={submitLoading} 
                            />
                        </div>
                    </>
                )}
                {formStep === 3 && (
                    <div className="mb-3">
                        <label htmlFor="formGuests" className="form-label fs-7 rakete-font">Anzahl</label>
                        <input 
                            ref={guestsInputRef} 
                            type="number" 
                            className="form-control" 
                            id="formGuests" 
                            name="guests" 
                            value={participantFormData.guests} 
                            onChange={handleFormChange} 
                            min="0" 
                            required 
                            disabled={submitLoading} 
                        />
                    </div>
                )}
                <div className="d-flex flex-column flex-sm-row justify-content-center gap-2 mt-4">
                    {formStep > 1 && (
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary rakete-font order-sm-1" 
                            onClick={handlePrevStep} 
                            disabled={submitLoading || stepLoading}
                        >
                            <IoMdArrowBack className="me-1" size={18} /> Zurück
                        </button>
                    )}
                    {formStep < 3 ? (
                        <button 
                            type="button" 
                            className="btn btn-danger rakete-font order-sm-2" 
                            onClick={handleNextStepButtonClick} 
                            disabled={submitLoading || stepLoading}
                        >
                            {stepLoading && formStep === 1 ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Prüfe...
                                </>
                            ) : (
                                <>Weiter <IoMdArrowForward className="ms-1" size={18} /></>
                            )}
                        </button>
                    ) : (
                        <button 
                            type="button" 
                            className="btn btn-danger rakete-font order-sm-2" 
                            onClick={handleFinalSubmitButtonClick} 
                            disabled={submitLoading || stepLoading}
                        >
                            {submitLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Speichern...
                                </>
                            ) : (
                                "Teilnehmen"
                            )}
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default ParticipantForm;
